import { useEffect, useState } from 'react';
import Head from 'next/head';
import orderBy from 'lodash/orderBy';
import { useRouter } from 'next/router';
import { gql, NetworkStatus, useQuery } from '@apollo/client';
import {
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Pagination as MuiPagination,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import DefaultTemplate from '@components/templates/DefaultTemplate';
import PageInsetTop from '@components/atoms/PageInsetTop';
import Container from '@components/atoms/Container';
import Hidden from '@components/atoms/Hidden';
import SearchBar from '@components/molecules/SearchBar';
import CarItem from '@components/molecules/Search/CarItem';
import PageItem from '@components/molecules/Search/PageItem';
import { trackEvent } from '@components/organisms/Tracking';
import { useTranslation } from 'next-i18next';
import { getAppData } from '@lib/appInitialState';

export default function Search() {
  const theme = useTheme();
  const router = useRouter();
  const {
    t,
    i18n,
  } = useTranslation();
  const { q } = router.query;
  const [filter, setFilter] = useState('all');
  const skip = parseInt(router.query.skip, 10) || 0;
  const take = filter === 'all' ? 5 : 10;

  useEffect(() => {
    if (q) {
      trackEvent('Search', { search_string: q });
    }
  }, [q]);

  const {
    data,
    loading,
  } = useQuery(FETCH_RESULTS, {
    variables: {
      searchInput: q ?? '',
      skip,
      take,
      filter,
      locale: i18n.language,
      localeStr: i18n.language,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: NetworkStatus.loading,
  });
  const items = orderBy([
    ...data?.search?.pages.data ?? [],
    ...data?.search?.cars.data ?? [],
  ], ['attributes.search_score'], ['desc']);
  const itemCount = Math.ceil(Math.max(
    data?.search?.totalHitsPages || 0,
    data?.search?.totalHitsCars || 0,
  ) / take);
  const itemPage = Math.ceil(skip / take) + 1;

  const handleChangeTab = (event, tab) => {
    setFilter(tab);

    const url = `?q=${encodeURIComponent(q)}&skip=0`;
    router.replace(url, undefined, { shallow: true });
  };
  const handleSearch = (value) => {
    const url = `?q=${encodeURIComponent(value)}`;
    window.scrollTo(0, 0);
    router.replace(url, undefined, { shallow: true });
  };
  const handleChangePage = (event, page) => {
    event.preventDefault();
    event.stopPropagation();

    const url = `?q=${encodeURIComponent(q)}&skip=${(page - 1) * take}`;
    window.scrollTo(0, 0);
    router.replace(url, undefined, { shallow: true });
  };
  const handleSearchFrequentItem = (value) => () => {
    window.dispatchEvent(new CustomEvent('doSearch', { detail: value }));
  };

  return (
    <>
      <Head>
        <title>
          {t('pages.search.search')}
        </title>
        <meta
          content={t('pages.search.metaDescription')}
          name="description"
        />
        <meta content={t('pages.search.search')} property="og:title" />
        {q && (
          <meta content="noindex, follow" name="robots" />
        )}
      </Head>
      <DefaultTemplate>
        <Box>
          <Container>
            <PageInsetTop />
            <Hidden dir="up" size="md">
              {!q && (
                <Box pb={2}>
                  <Typography gutterBottom variant="h1">
                    {t('pages.search.search')}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: theme.spacing(2),
                  '& > *': {
                    flex: 1,
                  },
                  [theme.breakpoints.up('md')]: {
                    '& > *': {
                      flex: '0 1 600px',
                    },
                  },
                }}
              >
                <SearchBar
                  autofocus
                  loading={loading}
                  onSubmit={handleSearch}
                  search={data?.search?.searchInput ?? q}
                  t={t}
                />
              </Box>
              {!q && (
                <Box pb={7} pt={3}>
                  <Typography variant="subtitle2">
                    {t('pages.search.frequentlySearchedFor')}
                  </Typography>
                  <List>
                    {FREQUENTLY_SEARCHES.map((s) => (
                      <ListItem key={s} button onClick={handleSearchFrequentItem(s)}>
                        <ListItemText primary={s} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Hidden>
            <Hidden dir="down" size="sm">
              <Box
                alignItems="center"
                display="flex"
                sx={{
                  marginBottom: theme.spacing(1.5),
                }}
              >
                <Typography variant="h1">
                  {t('pages.search.searchResults')}
                </Typography>
                {loading && (
                  <CircularProgress
                    size={30}
                    sx={{
                      marginLeft: theme.spacing(2),
                    }}
                  />
                )}
              </Box>
            </Hidden>
            {q && (
              <Tabs
                indicatorColor="primary"
                onChange={handleChangeTab}
                value={filter}
                variant="scrollable"
              >
                <Tab label={t('pages.search.everything')} value="all" />
                <Tab label={t('pages.search.advertisements')} value="cars" />
                <Tab label={t('pages.search.newsAndActions')} value="blog" />
                <Tab label={t('pages.search.contents')} value="content" />
              </Tabs>
            )}
          </Container>
        </Box>
        {q && (
          <Box
            sx={{
              background: theme.palette.quaternary.main,
              borderTopStyle: 'solid',
              borderTopColor: theme.palette.tertiary.main,
              borderTopWidth: 1,
            }}
          >
            <Container>
              <Box>
                {items.map((item) => {
                  // eslint-disable-next-line no-underscore-dangle
                  const Component = TYPE_MAP[item.__typename];

                  return (
                    <Box key={item.id} mt={2}>
                      <Component id={item.id} {...item.attributes} />
                    </Box>
                  );
                })}
              </Box>
              {!loading && itemCount === 0 && (
                <Box mt={5}>
                  <Typography
                    align="center"
                    sx={{
                      opacity: 0.5,
                    }}
                  >
                    {t('pages.search.noSearchResultsFound')}
                  </Typography>
                </Box>
              )}
              {!loading && itemCount > 0 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <MuiPagination
                    count={itemCount}
                    onChange={handleChangePage}
                    page={itemPage}
                    showFirstButton
                    showLastButton
                    siblingCount={0}
                  />
                </Box>
              )}
            </Container>
          </Box>
        )}
      </DefaultTemplate>
    </>
  );
}

const TYPE_MAP = {
  PageEntity: PageItem,
  InventoryCarEntity: CarItem,
};

const FREQUENTLY_SEARCHES = [
  'Werkstatt-Terminplaner',
  'Hyundai IONIQ 5',
  'Mitsubishi Space Star',
];

export async function getServerSideProps(ctx) {
  const {
    query,
    res,
    locale,
  } = ctx;
  const [graphQlClient, appData] = await getAppData(ctx);

  return {
    props: {
      ...appData,
      suppressCanonical: Boolean(query.q),
    },
  };
}

const FETCH_RESULTS = gql`
  query FetchSearchResults($searchInput: String!, $skip: Int!, $take: Int!, $filter: String, $locale: I18NLocaleCode, $localeStr: String!) {
    search(searchInput: $searchInput, skip: $skip, take: $take, filter: $filter, locale: $locale) {
      searchInput
      skip
      take
      totalHitsPages
      totalHitsCars
      pages {
        data {
          ${PageItem.graphQlProps}
        }
      }
      cars {
        data {
          ${CarItem.graphQlProps}
        }
      }
    }
  }
`;
